<template>
  <v-container fluid tag="section">
    <material-card full-header>
      <template #heading>
        <v-toolbar color="accent">
          <v-toolbar-title>
            <v-icon> mdi-server </v-icon>
          </v-toolbar-title>
          <v-divider vertical></v-divider>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn text icon dark class="mb-2" v-bind="attrs" v-on="on" @click="getServers">
                <v-icon medium>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>
              {{ $t("refresh") }}
            </span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn text icon dark class="mb-2" v-bind="attrs" v-on="on" @click="showFilter = !showFilter">
                <v-icon medium>mdi-filter</v-icon>
              </v-btn>
            </template>
            <span>
              {{ $t("filter") }}
            </span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn text icon dark class="mb-2" v-bind="attrs" v-on="on" @click="serverDialog = true"
                v-show="serverType == 'vm' && hasPermission('virtual_machine_store')">
                <v-icon medium>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>
              {{ $t("server_create") }}
            </span>
          </v-tooltip>
        </v-toolbar>
      </template>
      <v-data-table :items="serversList" :headers="showHeaders" :server-items-length="totalServers" :loading="loading"
        :options.sync="options" :items-per-page="itemPerPage" :page="pageNumber" item-key="id" disable-sort>
        <template v-slot:top>
          <v-expand-transition>
            <v-sheet class="ma-3 pa-3" v-show="showFilter" dense>
              <v-row dense flex>
                <v-col cols="12" lg="2" md="4">
                  <v-text-field v-model="domainFilter" @keydown.enter="getServers()" :label="$t('domain')" outlined
                    dense hide-details>
                  </v-text-field>
                </v-col>
                <v-col cols="12" lg="2" md="4">
                  <v-text-field v-model="ipFilter" @keydown.enter="getServers()" :label="$t('ip')" outlined dense
                    hide-details>
                  </v-text-field>
                </v-col>
                <v-col cols="12" lg="2" md="4" v-show="serverType == 'vm'">
                  <v-select :items="serviceNameSelect" v-model="serviceFilter" @keydown.enter="getServers()"
                    :label="$t('service_name')" outlined dense hide-details clearable>
                  </v-select>
                </v-col>
                <v-col cols="12" lg="1" md="4">
                  <v-btn rounded @click="getServers" color="primary">
                    <v-icon> mdi-filter </v-icon>
                    {{ $t("filter") }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-sheet>
          </v-expand-transition>
        </template>
        <!-- custom columns -->
        <template v-slot:[`item.service_name`]="{ item }">
          <span>{{ $t(item.service_name) }}</span>
        </template>

        <template v-slot:[`item.server_edit`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2" text icon v-bind="attrs" v-on="on" :disabled="!hasPermission('virtual_machine_update')">
                <v-icon color="primary" medium @click="editServer(item)">mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("server_edit") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.server_delete`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2" text icon v-bind="attrs" v-on="on" :disabled="!hasPermission('virtual_machine_delete')">
                <v-icon color="primary" medium @click="showDeleteServerDialog(item)">mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("server_delete") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.bbb_meetings`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2" text icon v-bind="attrs" v-on="on" :disabled="!hasPermission('virtual_machine_meeting_list')">
                <v-icon color="primary" medium @click="showMeetingsDialog(item)">mdi-google-classroom</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("meetings") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.bbb_clean`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2" text icon v-bind="attrs" v-on="on" :disabled="!hasPermission('command_bigbluebutton_clean')">
                <v-icon color="primary" medium @click="showBBBActionDialog(item, 'clean', $t('bbb_clean'))">
                  mdi-monitor-shimmer</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("bbb_clean") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.bbb_reset_free_switch`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2" text icon v-bind="attrs" v-on="on" :disabled="!hasPermission('command_bigbluebutton_freeswitch')">
                <v-icon color="primary" medium @click="
                  showBBBActionDialog(item, 'freeswitch', $t('bbb_reset_free_switch'))
                ">mdi-microphone-off</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("bbb_reset_free_switch") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.bbb_reset_kurento`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2" text icon v-bind="attrs" v-on="on" :disabled="!hasPermission('command_bigbluebutton_kurento')">
                <v-icon color="primary" medium @click="showBBBActionDialog(item, 'kurento', $t('bbb_reset_kurento'))">
                  mdi-video-off-outline </v-icon>
              </v-btn>
            </template>
            <span>{{ $t("bbb_reset_kurento") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.reboot`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2" text icon v-bind="attrs" v-on="on" :disabled="!hasPermission('virtual_machine_reboot')">
                <v-icon color="primary" medium @click="showVMRebootDialog(item)">mdi-lock-reset</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("reboot") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.bbbServers`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2" text icon v-bind="attrs" v-on="on" :disabled="!hasPermission('command_loadbalancer_servers')">
                <v-icon color="primary" medium @click="routeToBBBServers(item)">mdi-server</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("bbb_servers") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.lbFlushAll`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2" text icon v-bind="attrs" v-on="on" :disabled="!hasPermission('command_loadbalancer_flush_all')">
                <v-icon color="primary" medium @click="showLBFlushAllDialog(item)">mdi-cookie-refresh</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("lb_flush_all") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.lbRestart`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2" text icon v-bind="attrs" v-on="on" :disabled="!hasPermission('command_loadbalancer_restart')">
                <v-icon color="primary" medium @click="showLBRestartDialog(item)">mdi-lock-reset</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("lb_restart") }}</span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.more`]="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ma-2" text icon color="primary" v-bind="attrs" v-on="on">
                <v-icon medium>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-subheader class="justify-center">
                {{ item.domain }}
              </v-subheader>
              <v-divider></v-divider>
              <v-list-item v-show="serverType == 'bigbluebutton'" :disabled="!hasPermission('command_bigbluebutton_stop')">
                <v-list-item-title>
                  <v-btn text block @click="showBBBActionDialog(item, 'stop', $t('bbb_stop'))" color="primary">
                    Stop
                  </v-btn>
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-show="serverType == 'bigbluebutton'" :disabled="!hasPermission('command_bigbluebutton_start')">
                <v-list-item-title>
                  <v-btn text block @click="showBBBActionDialog(item, 'start', $t('bbb_start'))" color="primary">
                    Start
                  </v-btn>
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-show="serverType == 'bigbluebutton'" :disabled="!hasPermission('command_bigbluebutton_reset')">
                <v-list-item-title>
                  <v-btn text block @click="showBBBActionDialog(item, 'reset', $t('bbb_reset'))" color="primary">
                    Reset
                  </v-btn>
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-show="serverType == 'no'" :disabled="!hasPermission('virtual_machine_hardware_get')">
                <v-list-item-title>
                  <v-btn text block color="primary" @click="showServerCheckDialog(item)">
                    Check
                  </v-btn>
                </v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="!hasPermission('virtual_machine_hardware_get')">
                <v-list-item-title>
                  <v-btn text block color="primary" @click="showServerCheckHardwareDialog(item)">
                    Check Hardware
                  </v-btn>
                </v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="!hasPermission('virtual_machine_secret_get')">
                <v-list-item-title>
                  <v-btn text block color="primary" @click="showServerSecretDialog(item)">
                    Secret
                  </v-btn>
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-show="serverType == 'bigbluebutton'" :disabled="!hasPermission('command_bigbluebutton_show_raw_list')">
                <v-list-item-title>
                  <v-btn text block color="primary" @click="showRawFilesDialog(item)">
                    Raw Files
                  </v-btn>
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-show="serverType != 'vm'" :disabled="!hasPermission('virtual_machine_recording_list')">
                <v-list-item-title>
                  <v-btn text block color="primary" @click="
                    showPublishedFilesDialog(item)
                  ">
                    Published Recording
                  </v-btn>
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-show="serverType == 'bigbluebutton'" :disabled="!hasPermission('command_bigbluebutton_reconfigure')">
                <v-list-item-title>
                  <v-btn text block color="primary" @click="showBBBConfigDialog(item)">
                    {{ $t('bbb_config') }}
                  </v-btn>
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-show="serverType == 'vm'">
                <v-list-item-title>
                  <v-btn text block color="primary" @click="showInstallBBBDialog(item)"
                  :disabled="!hasPermission('command_bigbluebutton_install')">
                    {{ $t('bbb_install') }}
                  </v-btn>
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-show="serverType == 'vm'">
                <v-list-item-title>
                  <v-btn text block color="primary" @click="showInstallLBDialog(item)"
                  :disabled="!hasPermission('command_loadbalancer_install')">
                    {{ $t('lb_install') }}
                  </v-btn>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </material-card>
    <!-- Add server dialog -->
    <v-dialog v-model="serverDialog" width="450">
      <material-card color="primary" icon="mdi-server" iconSmall solo>
        <template #title>
          <span>{{ serverFormTitle }}</span>
        </template>
        <v-form ref="serverForm" lazy-validation persistent>
          <v-container>
            <v-row dense>
              <v-col cols="12">
                <v-select v-model="editedServer.service_id" :items="serviceNameSelect" :label="$t('service_name')">
                </v-select>
              </v-col>
              <v-col cols="12">
                <v-text-field class="text-content-left" v-model="editedServer.domain" :label="$t('domain')">
                </v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="editedServer.ip" :label="$t('ip')" class="text-content-left">
                </v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="editedServer.port" :label="$t('port')" class="text-content-left">
                </v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field class="text-content-left" v-model="editedServer.username" :label="$t('username')">
                </v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field class="text-content-left" v-model="editedServer.password" type="password"
                  :label="$t('password')">
                </v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
        <template #actions>
          <v-btn color="primary" text @click="saveServer" :loading="saveLoading" :disabled="!hasPermission('virtual_machine_store')">
            {{ $t("save") }}
          </v-btn>
          <v-spacer />
          <v-btn color="primary" text @click="closeServerDialog" :loading="saveLoading">
            {{ $t("close") }}
          </v-btn>
        </template>
      </material-card>
    </v-dialog>

    <!-- Secret dialog -->
    <v-dialog v-model="serverSecretDialog" width="550" :key="editedServer.id">
      <material-card color="primary" icon="mdi-server" iconSmall solo>
        <template #title>
          <span>{{ $t("bbb_secret") }}</span>
        </template>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" class="">
                <v-tooltip bottom color="primary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-on="on" v-bind="attrs" @click="copyToClipBoardSecret">
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  {{ $t("bbb_secret_copy") }}
                </v-tooltip>
                <span class="font-weight-bold"> Secret : {{ serverSecretInfo }} </span>
              </v-col>
              <v-col cols="12">
                <v-tooltip bottom color="primary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-on="on" v-bind="attrs" @click="copyToClipBoardUrl">
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  {{ $t("bbb_url_copy") }}
                </v-tooltip>
                <span class="font-weight-bold">
                  URL : {{ "https://" + editedServer?.domain + "/bigbluebutton" }}
                </span>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <template #actions>
          <v-btn color="primary" text @click="putServerSecret" :disabled="!hasPermission('virtual_machine_reload_secret')">{{ $t("refresh") }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="openApiMate">{{
              $t("bbb_api_mate")
          }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeServerSecretDialog">{{
              $t("close")
          }}</v-btn>
        </template>
      </material-card>
    </v-dialog>
    <!-- Check Hardware dialog -->
    <v-dialog v-model="serverCheckHardwareDialog" width="550">
      <material-card color="primary" icon="mdi-harddisk" iconSmall solo>
        <template #title>
          <span> {{ $t('server_check_info') }} {{ editedServer?.domain }}</span>
        </template>
        <template #subTitle>
          <span> {{ editedServer?.ip }}</span>
        </template>
        <v-card-text>
          <v-simple-table>
            <tbody>
              <tr v-for="(value, text)  in serverCheckHardwareInfo" :key="text">
                <td v-show="text != 'id'" class="text-center">
                  {{ $t('server_' + text) }}:
                </td>
                <td v-show="text != 'id'" class="text-center">
                  {{ value }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <template #actions>
          <v-btn color="primary" text @click="runServerCheckHardwareScript"
          :disabled="!hasPermission('virtual_machine_hardware_reload')"
          >{{ $t("run_hardware_script") }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeServerCheckHardwareDialog">{{
              $t("close")
          }}</v-btn>
        </template>
      </material-card>
    </v-dialog>
    <v-dialog v-model="bbbConfigDialog" width="600" scrollable>
      <material-card color="primary" icon="mdi-cog-outline" iconSmall solo>
        <template #title>
          <span> {{ $t('bbb_config') }} {{ editedServer?.domain }}</span>
        </template>
        <template #text>
          <v-container>
            <v-form ref="bbbConfigForm">
              <v-row dense>
                <v-col cols="12">
                  <v-text-field v-model="bbbConfigInfo.app_name" :label="$t('bbb_app_name')">
                  </v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="bbbConfigInfo.client_title" :label="$t('bbb_client_title')">
                  </v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="bbbConfigInfo.default_welcome_message" :label="$t('bbb_welcome_message')">
                  </v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="bbbConfigInfo.default_welcome_message_footer"
                    :label="$t('bbb_welcome_message_footer')">
                  </v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="bbbConfigInfo.copyright" :label="$t('bbb_copy_right')">
                  </v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="bbbConfigInfo.guide_url" :label="$t('bbb_guid_link')">
                  </v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-select v-model="bbbConfigInfo.language" :label="$t('bbb_language')" :items="lang">
                  </v-select>
                </v-col>
                <v-col cols="12">
                  <v-select v-model="bbbConfigInfo.styles" :label="$t('bbb_styles')" :items="bbbStyles" item-text="name"
                    item-value="id">
                  </v-select>
                </v-col>
                <v-col cols="12">
                  <v-file-input v-model="bbbConfigInfo.default_presentation"
                    :label="$t('bbb_default_presentation_file')" accept=".pdf">
                  </v-file-input>
                </v-col>
                <v-col cols="12">
                  <v-file-input v-model="bbbConfigInfo.favicon" :label="$t('bbb_fav_icon')" accept=".ico">
                  </v-file-input>
                </v-col>
                <v-col cols="12">
                  <v-switch v-model="bbbConfigInfo.camStatus" :label="$t('bbb_cam_status')"></v-switch>
                </v-col>
                <v-col cols="12">
                  <v-switch v-model="bbbConfigInfo.micStatus" :label="$t('bbb_mic_status')"></v-switch>
                </v-col>
                <v-col cols="12">
                  <v-switch v-model="bbbConfigInfo.noteStatus" :label="$t('bbb_note_status')"></v-switch>
                </v-col>
                <v-col cols="12">
                  <v-switch v-model="bbbConfigInfo.duplicateStatus" :label="$t('bbb_duplicate_status')"></v-switch>
                </v-col>
                <v-col cols="12">
                  <v-switch v-model="bbbConfigInfo.privateChatStatus" :value="true"
                    :label="$t('bbb_private_chat_status')"></v-switch>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </template>
        <template #actions>
          <v-btn color="primary" text @click="saveBBBConfig" :loading="bbbConfigLoading">{{ $t("save") }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeBBBConfigDialog" :loading="bbbConfigLoading">{{
              $t("close")
          }}</v-btn>
        </template>
      </material-card>
    </v-dialog>
    <v-dialog v-model='meetingsDialog' :key="editedServer.id" scrollable>
      <BBBLiveMeetings :server-data="editedServer"  @close="closeMeetingsDialog" />
    </v-dialog>
    <v-dialog v-model='rawFilesDialog' :key="editedServer.id" scrollable>
      <BBBRawFiles :server-data="editedServer"  @close="closeRawFilesDialog" />
    </v-dialog>
    <v-dialog v-model='publishedFilesDialog' :key="editedServer.id" scrollable>
      <BBBPublishedFiles :server-data="editedServer"  @close="closePublishedFilesDialog" />
    </v-dialog>
    <AskDialog :visible="deleteServerDialog" yes no :loading="deleteLoading" @yesClick="deleteServer"
      @noClick="closeDeleteServerDialog" :text="deleteServerDialogText"></AskDialog>
    <AskDialog :visible="bbbActionDialog" yes no :loading="bbbActionLoading" @yesClick="bbbAction"
      @noClick="closeBBBActionDialog" :text="bbbActionDialogText"></AskDialog>
    <AskDialog :visible="vmRebootDialog" yes no :loading="vmRebootLoading" @yesClick="vmReboot"
      @noClick="closeVMRebootDialog" :text="vmRebootDialogText"></AskDialog>

    <AskDialog :visible="lbRestartDialog" yes no @yesClick="lbRestart" @noClick="closeLBRestartDialog"
      :text="lbRestartDialogText"></AskDialog>

    <AskDialog :visible="lbFlushAllDialog" yes no @yesClick="lbFlushAll" @noClick="closeLBFlushAllDialog"
      :text="lbFlushAllDialogText"></AskDialog>

    <AskDialog :visible="installBBBDialog" yes no @yesClick="installBBB" @noClick="closeInstallBBBDialog"
      :text="installBBBDialogText"></AskDialog>
    <AskDialog :visible="installLBDialog" yes no @yesClick="installLB" @noClick="closeInstallLBDialog"
      :text="installLBDialogText"></AskDialog>
  </v-container>
</template>
<script>
import permissions from '../mixins/permissions';
import * as DOMPurify from 'dompurify';

export default {
  name: "VmServers",
  mixins:[permissions],
  props: {
    serverType: {
      type: String,
      default: "vm",
    },
  },
  computed: {
    showHeaders() {
      return this.serversHeader.filter((f) => {
        return f.show == undefined || f.show;
      });
    },
    tableTitle() {
      if (this.serverType == "bigbluebutton") return this.$t("bigbluebutton_server");
      else if (this.serverType == "loadbalancer") return this.$t("loadbalancer_server");
      else return this.$t("vm");
    },
    serverFormTitle() {
      return this.editedServerIndex == -1
        ? this.$t("server_create")
        : this.$t("server_edit");
    },
  },
  data() {
    return {
      serverDialog: false,
      server: {},
      editedServer: {},
      editedServerIndex: -1,
      serversHeader: [
        { value: "domain", text: this.$t("domain") },
        { value: "ip", text: this.$t("ip") },
        {
          value: "service_name",
          text: this.$t("service_name"),
          show: this.serverType == "vm",
        },
        {
          value: "status_meetings",
          text: this.$t("meeting"),
          show: this.serverType != "vm",
        },
        {
          value: "status_users",
          text: this.$t("user"),
          show: this.serverType != "vm",
        },
        {
          value: "status_video",
          text: this.$t("video"),
          show: this.serverType != "vm",
        },
        {
          value: "status_voice",
          text: this.$t("voice"),
          show: this.serverType != "vm",
        },
        {
          value: "username",
          text: this.$t("username"),
          show: this.serverType == "vm",
        },
        { value: "port", text: this.$t("port"), show: this.serverType == "vm" },
        {
          value: "server_edit",
          text: this.$t("server_edit"),
          show: this.serverType == "vm",
        },
        {
          value: "server_delete",
          text: this.$t("server_delete"),
          show: this.serverType == "vm",
        },
        {
          value: "bbb_meetings",
          text: this.$t("meetings"),
          show: this.serverType != "vm",
        },
        {
          value: "bbb_clean",
          text: this.$t("bbb_clean"),
          show: this.serverType == "bigbluebutton",
        },
        {
          value: "bbb_reset_free_switch",
          text: this.$t("bbb_reset_free_switch"),
          show: this.serverType == "bigbluebutton",
        },
        {
          value: "bbb_reset_kurento",
          text: this.$t("bbb_reset_kurento"),
          show: this.serverType == "bigbluebutton",
        },
        {
          value: "reboot",
          text: this.$t("reboot"),
        },
        {
          value: "bbbServers",
          text: this.$t("bbb_servers"),
          show: this.serverType == "loadbalancer",
        },
        {
          value: "lbFlushAll",
          text: this.$t("lb_flush_all"),
          show: this.serverType == "loadbalancer",
        },
        {
          value: "lbRestart",
          text: this.$t("lb_restart"),
          show: this.serverType == "loadbalancer",
        },
        {
          value: "more",
          text: this.$t("more"),
        },
      ],
      serviceNameSelect: [],
      serversList: [],
      loading: true,
      totalServers: 0,
      options: {},
      itemPerPage: 10,
      pageNumber: 1,
      startRow: 0,
      endRow: 10,
      showFilter: true,
      domainFilter: "",
      ipFilter: "",
      serviceFilter: "",
      saveLoading: false,
      deleteServerDialog: false,
      deleteServerDialogText: "",
      deleteLoading: false,
      bbbActionDialog: false,
      bbbActionName: "",
      bbbActionLoading: false,
      bbbActionDialogText: "",
      bbbActionTitle: "",
      vmRebootDialog: false,
      vmRebootLoading: false,
      vmRebootDialogText: "",
      lbFlushAllDialogText: "",
      lbRestartDialogText: "",
      serverSecretDialog: false,
      serverSecretInfo: "",
      serverCheckHardwareInfo: "",
      serverCheckHardwareDialog: false,
      meetingsDialog: false,
      rawFilesDialog: false,
      publishedFilesDialog: false,
      bbbConfigDialog: false,
      bbbConfigInfo: {},
      lang: [
        { text: this.$t('persian'), value: 'fa' },
        { text: this.$t('english'), value: 'en' },
        { text: this.$t('arabic'), value: 'ar' },
        { text: this.$t('turkey'), value: 'tr' }
      ],
      bbbConfigLoading: false,
      bbbStyles: [],
      lbFlushAllDialog: false,
      lbRestartDialog: false,
      installBBBDialogText: '',
      installBBBDialog: false,
      installLBDialogText: '',
      installLBDialog: false
    };
  },
  watch: {
    options: {
      handler() {
        this.getServers();
      },
      deep: true,
    },
  },
  methods: {
    getServers() {
      const { page, itemsPerPage } = this.options;
      let _startRow = itemsPerPage * page - itemsPerPage;
      let _endRow = itemsPerPage * page - 1;
      if (_endRow < 0) _endRow = this.totalServers;
      const payload = { _startRow: _startRow, _endRow: _endRow };
      if (this.domainFilter != "") payload["domain"] = this.domainFilter;
      if (this.ipFilter != "") payload["ip"] = this.ipFilter;
      if (this.serviceFilter != "") payload["service_id"] = this.serviceFilter;

      if (this.serverType != "vm") payload["service_name"] = this.serverType;
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/virtual_machine",
        method: "GET",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
        params: payload,
      })
        .then((response) => {
          const data = response.data.response;
          this.serversList = data.data;
          this.totalServers = data.totalRows;
          this.loading = false;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    closeServerDialog() {
      this.editedServerIndex = -1;
      this.editedServer = { service_id: null };
      this.serverDialog = false;
    },
    editServer(item) {
      this.editedServerIndex = this.serversList.indexOf(item);
      this.editedServer = Object.assign({}, item);
      this.serverDialog = true;
    },
    getServices() {
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/service",
        method: "GET",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
      })
        .then((response) => {
          const data = response.data.response;

          data.data.forEach((element) => {
            this.serviceNameSelect.push({
              text: this.$t(element.name),
              value: element.id,
            });
          });
        })
        .catch((error) => {
          console.log(error);
        });
    },
    saveServer() {
      if (!this.$refs.serverForm.validate()) {
        this.$toasted.error(this.$t("fill_the_form"), {
          icon: { name: "alert-circle" },
        });
      } else {
        this.saveLoading = true;
        let payload = { ...this.sanitizeObject(this.editedServer) };
        axios({
          url:
            process.env.VUE_APP_BACKEND_URL + "/api/virtual_machine" +
            (this.editedServerIndex == -1 ? "" : "/" + this.editedServer.id),
          method: this.editedServerIndex == -1 ? "POST" : "PUT",
          headers: {
            // 'Content-Type': 'multipart/form-data',
            Authorization: this.$store.get("login/token"),
          },
          data: payload,
        })
          .then((response) => {
            this.getServers();
            this.$toasted.success(
              this.editedServerIndex == -1
                ? this.$t("server_created_successfully")
                : this.$t("server_edited_successfully")
            );
            this.closeServerDialog();
          })
          .catch((error) => {
            console.log(error.response);
            debugger;
            this.$toasted.error(this.$t("server_error_saving"), {
              icon: { name: "alert-circle", after: true },
            });
          })
          .finally(() => {
            this.saveLoading = false;
          });
      }
    },
    deleteServer() {
      this.deleteLoading = true;
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/virtual_machine/" + this.editedServer.id,
        method: "DELETE",
        headers: {
          // 'Content-Type': 'multipart/form-data',
          Authorization: this.$store.get("login/token"),
        },
      })
        .then((response) => {
          this.closeDeleteServerDialog();
          this.getServers();
          this.$toasted.success(this.$t("server_deleted_successfully"));
        })
        .catch((error) => {
          console.log(error.response);
          this.$toasted.error(this.$t("server_error_deleting"));
        })
        .finally(() => {
          this.deleteLoading = false;
        });
    },
    showDeleteServerDialog(item) {
      this.editedServer = Object.assign({}, item);
      this.deleteServerDialogText = this.$t("server_delete_ask", {
        domain: this.editedServer?.domain,
      });
      this.deleteServerDialog = true;
    },
    closeDeleteServerDialog() {
      this.editedServer = {};
      this.deleteServerDialog = false;
      this.deleteServerDialogText = "";
    },
    showBBBActionDialog(item, action, actionTitle) {
      this.bbbActionName = action;
      this.bbbActionTitle = actionTitle;
      this.bbbActionDialogText = this.$t("bbb_action_ask", {
        action: actionTitle,
        domain: item.domain,
      });
      this.bbbActionDialog = true;
      this.editedServer = Object.assign({}, item);
    },
    closeBBBActionDialog() {
      this.bbbActionName = "";
      this.bbbActionTitle = "";
      this.bbbActionDialogText = "";
      this.bbbActionDialog = false;
      this.editedServer = {};
    },
    showVMRebootDialog(item) {
      this.vmRebootDialogText = this.$t("vm_reboot_ask1", {
        domain: item.domain,
      });
      this.vmRebootDialog = true;
      this.editedServer = Object.assign({}, item);
    },
    closeVMRebootDialog() {
      this.vmRebootDialogText = "";
      this.vmRebootDialog = false;
      this.editedServer = {};
    },
    showLBRestartDialog(item) {
      this.lbRestartDialogText = this.$t("lb_restart_dialog_text", {
        domain: item.domain,
      });

      this.lbRestartDialog = true;
      this.editedServer = Object.assign({}, item);
    },
    closeLBRestartDialog() {
      this.lbRestartDialogText = "";
      this.lbRestartDialog = false;
      this.editedServer = {};
    },
    showLBFlushAllDialog(item) {
      this.lbFlushAllDialogText = this.$t("lb_flush_all_dialog_text", {
        domain: item.domain,
      });
      this.lbFlushAllDialog = true;
      this.editedServer = Object.assign({}, item);
    },
    closeLBFlushAllDialog() {
      this.lbFlushAllDialogText = "";
      this.lbFlushAllDialog = false;
      this.editedServer = {};
    },
    bbbAction() {
      this.bbbActionLoading = true;
      axios({
        url:
          "/api/command/bigbluebutton/" + this.bbbActionName + "/" + this.editedServer.id,
        method: "POST",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
      })
        .then(() => {
          this.$toasted.success(
            this.$t("bbb_action_success", {
              action: this.bbbActionTitle,
              server: this.editedServer.domain,
            })
          );
          this.closeBBBActionDialog();
        })
        .finally(() => {
          this.bbbActionLoading = false;
        });
    },
    vmReboot() {
      this.vmRebootLoading = true;
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/command/reboot/" + this.editedServer.id,
        method: "Get",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
      })
        .then(() => {
          this.$toasted.success(
            this.$t("vm_reboot_success", {
              domain: this.editedServer.domain,
            })
          );
          this.closeVMRebootDialog();
        })
        .catch((error) => {
          console.log('error', error);
          this.$toasted.error(
            this.$t("vm_reboot_error", {
              domain: this.editedServer.domain,
            })
          );
        })
        .finally(() => {
          this.vmRebootLoading = false;
        });
    },
    lbRestart() {
      this.lbRestartDialog = false;
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/command/loadbalancer/restart/" + this.editedServer.id,
        method: "POST",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
      })
        .then(() => {
          this.$toasted.success(
            this.$t("lb_restart_success", {
              domain: this.editedServer.domain,
            })
          );

        })
        .catch((error) => {
          console.log('error', error);
          this.$toasted.error(
            this.$t("lb_restart_error", {
              domain: this.editedServer.domain,
            })
          );
        })
        .finally(() => {
          this.closeLBRestartDialog();
        });
    },
    lbFlushAll() {
      this.lbFlushAllDialog = false;
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/command/loadbalancer/flush_all/" + this.editedServer.id,
        method: "POST",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
      })
        .then(() => {
          this.$toasted.success(
            this.$t("lb_flush_all_success", {
              domain: this.editedServer.domain,
            })
          );

        }).catch((error) => {
          console.log('error', error);
          this.$toasted.error(
            this.$t("lb_flush_all_error", {
              domain: this.editedServer.domain,
            })
          );
        })
        .finally(() => {
          this.closeLBFlushAllDialog();
        });
    },
    showInstallBBBDialog(item) {
      this.installBBBDialogText = this.$t("bbb_install_dialog_text", {
        domain: item.domain,
      });
      this.installBBBDialog = true;
      this.editedServer = Object.assign({}, item);
    },
    closeInstallBBBDialog() {
      this.installBBBDialogText = "";
      this.installBBBDialog = false;
      this.editedServer = {};
    },
    installBBB() {
      this.installBBBDialog = false;
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/command/bigbluebutton/bbb_install/" + this.editedServer.id,
        method: "POST",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
      })
        .then(() => {
          this.$toasted.success(
            this.$t("bbb_install_request_success", {
              domain: this.editedServer.domain,
            })
          );

        }).catch((error) => {
          console.log('error', error);
          this.$toasted.error(
            this.$t("bbb_install_request_error", {
              domain: this.editedServer.domain,
            })
          );
        })
        .finally(() => {
          this.closeBBBInstallDialog();
        });
    },
    showInstallLBDialog(item) {
      this.installLBDialogText = this.$t("lb_install_dialog_text", {
        domain: item.domain,
      });
      this.installLBDialog = true;
      this.editedServer = Object.assign({}, item);
    },
    closeInstallLBDialog() {
      this.installLBDialogText = "";
      this.installLBDialog = false;
      this.editedServer = {};
    },
    installLB() {
      this.installLBDialog = false;
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/command/bigbluebutton/loadbalancer_install/" + this.editedServer.id,
        method: "POST",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
      })
        .then(() => {
          this.$toasted.success(
            this.$t("lb_install_request_success", {
              domain: this.editedServer.domain,
            })
          );

        }).catch((error) => {
          console.log('error', error);
          this.$toasted.error(
            this.$t("lb_install_request_error", {
              domain: this.editedServer.domain,
            })
          );
        })
        .finally(() => {
          this.closeLBInstallDialog();
        });
    },
    copyToClipBoardSecret() {
      navigator.clipboard.writeText(this.serverSecretInfo);
      this.$toasted.info(this.$t("bbb_secret_copied"));
    },
    copyToClipBoardUrl() {
      navigator.clipboard.writeText(
        "https://" + this.editedServer.domain + "/bigbluebutton/"
      );
      this.$toasted.info(this.$t("bbb_url_copied"));
    },
    openApiMate() {
      window.open(
        "https://mconf.github.io/api-mate/#server=" +
        "https://" +
        this.editedServer.domain +
        "/bigbluebutton/" +
        "&sharedSecret=" +
        this.serverSecretInfo,
        "_blank"
      );
    },
    showServerSecretDialog(item) {
      this.editedServer = Object.assign({}, item);
      this.serverSecretDialog = true;
      this.getServerSecret();
    },
    closeServerSecretDialog() {
      this.editedServer = {};
      this.serverSecretDialog = false;
      this.serverSecretInfo = "";
    },
    putServerSecret() {
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/command/secret/" + this.editedServer.id,
        method: "PUT",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
      }).then(() => {
        this.$toasted.success(this.$t("server_secret_put_request_sent"));
        setTimeout(() => {
          this.getServerSecret();
        }, 4000);
      });
    },
    getServerSecret() {
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/command/secret/" + this.editedServer.id,
        method: "GET",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
      }).then((response) => {
        this.serverSecretInfo = response.data.message;
      });
    },
    getServerCheckHardware() {
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/command/hardware/" + this.editedServer.id,
        method: "GET",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
      }).then((response) => {
        this.serverCheckHardwareInfo = response.data.data;
      });
    },
    runServerCheckHardwareScript() {
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/command/hardware/" + this.editedServer.id,
        method: "POST",
        headers: {
          Authorization: this.$store.get("login/token"),
        },
      }).then((response) => {
        this.$toasted.success(this.$t("server_check_hardware_post_request_sent"));
      });
    },
    showServerCheckHardwareDialog(item) {
      this.editedServer = Object.assign({}, item);
      this.getServerCheckHardware();
      this.serverCheckHardwareDialog = true;
    },
    closeServerCheckHardwareDialog() {
      this.serverCheckHardwareDialog = false;
      this.serverCheckHardwareInfo = null;
      this.editedServer = {};
    },
    showMeetingsDialog(item) {
      this.editedServer = Object.assign({}, item);
      this.meetingsDialog = true;
    },
    closeMeetingsDialog() {
      this.meetingsDialog = false;
    },
    showRawFilesDialog(item) {
      this.editedServer = Object.assign({}, item);
      this.rawFilesDialog = true;
    },
    closeRawFilesDialog() {
      this.rawFilesDialog = false;
    },
    showPublishedFilesDialog(item) {
      this.editedServer = Object.assign({}, item);
      this.publishedFilesDialog = true;
    },
    closePublishedFilesDialog() {
      this.publishedFilesDialog = false;
    },
    saveBBBConfig() {
      this.bbbConfigLoading = true;
      const payload = { ...this.bbbConfigInfo, bigbluebutton_ids: this.editedServer.id };
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/command/bigbluebutton/reconfigure",
        method: "POST",
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: this.$store.get("login/token"),
        },
        data: payload,
      }).then((response) => {
        this.closeBBBConfigDialog();
        this.$toasted.success(this.$t('bbb_config_save_success'))

      }).catch((error) => {
        console.log(error);
        this.$toasted.error(this.$t('bbb_config_save_error'))
      }).finally(() => {
        this.bbbConfigLoading = false;
      });
    },
    showBBBConfigDialog(item) {
      this.getBBBStyles();
      this.editedServer = item;
      this.bbbConfigInfo = {};
      this.bbbConfigDialog = true;
    },
    closeBBBConfigDialog() {
      this.bbbConfigDialog = false;
      this.bbbConfigInfo = {}
    },
    getBBBStyles() {
      // let formData=new FormData(this.$refs.bbbConfigForm);
      // console.log('fff',formData);
      axios({
        url: process.env.VUE_APP_BACKEND_URL + "/api/config/style/list",
        method: "GET",
        headers: {

          Authorization: this.$store.get("login/token"),
        },
        params: { _startRow: 0, _endRow: 1000 }
      })
        .then((response) => {
          this.bbbStyles = response.data.response.data;
        })
        .catch((error) => {
          console.log('error', error);
        }).finally(() => {
          this.loading = false;
        });
    },
    routeToBBBServers(server) {
      this.$router.push({ name: 'LB_BBB', params: { 'lbId': server.id } })
    },
    sanitizeObject(obj){
            for(let key in obj){
                obj[key]=DOMPurify.sanitize(obj[key]);
            }
            return obj;
    }
  },
  mounted() {
    this.getServices();
  },
};
</script>

